import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

const MINUTE = 60 * 1000;

/**
 * Urgency line of the selected variant (`blocks/stock-urgency.liquid`).
 *
 * - Low stock: Liquid renders the final text from the variant's real inventory.
 * - Otherwise: Liquid renders two templates (`data-today`, `data-next`) and this component picks one
 *   from the store clock — before the cut-off hour on a weekday the order is prepared today; after
 *   it, or at weekends, on the next weekday, named with `Intl` in the page locale. Store time comes
 *   from `data-utc-offset`, the same approach as `delivery-estimate.js`. Re-checked every minute.
 *
 * When another variant is picked, the variant picker fetches the section again and hands the new
 * HTML through `productSelect`; the component copies its own block from it and re-applies the clock.
 */
class StockUrgency extends Component {
  /** @type {Element | null} */
  #scope = null;

  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();
    this.#scope = this.closest('.shopify-section, dialog');
    this.#scope?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#update();
    this.#timer = window.setInterval(() => this.#update(), MINUTE);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#scope?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#scope = null;
    window.clearInterval(this.#timer);
  }

  get #offsetMinutes() {
    const match = /^([+-])(\d{2})(\d{2})$/.exec(this.dataset.utcOffset ?? '');
    if (!match) return -new Date().getTimezoneOffset();
    const sign = match[1] === '-' ? -1 : 1;
    return sign * (Number(match[2]) * 60 + Number(match[3]));
  }

  /** Current time as a "wall clock" date in the store's time zone. */
  get #storeNow() {
    const now = new Date();
    return new Date(now.getTime() + (this.#offsetMinutes + now.getTimezoneOffset()) * MINUTE);
  }

  /** @param {Date} date */
  #isWeekday(date) {
    const day = date.getDay();
    return day !== 0 && day !== 6;
  }

  /** @param {Date} date */
  #weekdayName(date) {
    try {
      return new Intl.DateTimeFormat(this.dataset.locale || undefined, { weekday: 'long' }).format(date);
    } catch (error) {
      return '';
    }
  }

  #update() {
    const text = /** @type {HTMLElement | null} */ (this.querySelector('.stock-urgency__text[data-today]'));
    if (!text) return;

    const now = this.#storeNow;
    const cutoffHour = Number(this.dataset.cutoffHour ?? 12);
    const hourLabel = `${String(cutoffHour).padStart(2, '0')}:00`;

    let message = '';
    if (this.#isWeekday(now) && now.getHours() < cutoffHour) {
      message = (text.dataset.today ?? '').replace('[hour]', hourLabel);
    } else {
      const next = new Date(now);
      do {
        next.setDate(next.getDate() + 1);
      } while (!this.#isWeekday(next));
      message = (text.dataset.next ?? '').replace('[day]', this.#weekdayName(next)).replace('[hour]', hourLabel);
    }

    if (text.textContent !== message) text.textContent = message;
    text.closest('.stock-urgency__inner')?.toggleAttribute('hidden', message === '');
  }

  /**
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const selector = `stock-urgency[data-block-id="${CSS.escape(this.dataset.blockId ?? '')}"]`;
        const next = html.querySelector(selector);
        if (!next) return;

        morph(this, next, { childrenOnly: true });
        this.#update();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[stock-urgency] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('stock-urgency')) {
  customElements.define('stock-urgency', StockUrgency);
}
